import { IOverlayObject } from '../display';

import { IViewport } from '.';

export class OverlayLayer {
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private objects: Map<string, IOverlayObject> = new Map();
    private cssWidth = 0;
    private cssHeight = 0;
    private dpr = 1;

    constructor(private viewport: IViewport) {
        this.canvas = this.createCanvas(viewport.container);
        this.ctx = this.canvas.getContext('2d')!;
    }

    createCanvas(container: HTMLElement) {
        const canvas = document.createElement('canvas');
        canvas.style.position = 'absolute';
        canvas.style.top = '0';
        canvas.style.left = '0';
        canvas.style.cursor = 'default';
        canvas.style.zIndex = '10';

        container.classList.add('relative');
        container.appendChild(canvas);

        return canvas;
    }

    addOverlayObject(obj: IOverlayObject) {
        if (this.objects.has(obj.id)) {
            throw new Error(`Feature "${obj.id}" is already registered`);
        }
        this.objects.set(obj.id, obj);
    }

    removeOverlayObject(id: string) {
        const obj = this.objects.get(id);
        if (obj) {
            this.objects.delete(id);
        }
    }

    invertYAxisCoordinates(v: number[]): number[] {
        return [v[0], this.cssHeight - v[1]];
    }

    getCssWidth(): number {
        return this.cssWidth;
    }

    getCssHeight(): number {
        return this.cssHeight;
    }

    getDpr(): number {
        return this.dpr;
    }

    getCanvas(): HTMLCanvasElement {
        return this.canvas;
    }

    resize(width: number, height: number, devicePixelRatio: number = 1): void {
        this.cssWidth = width;
        this.cssHeight = height;
        this.dpr = devicePixelRatio;

        this.canvas.style.width = `${width}px`;
        this.canvas.style.height = `${height}px`;

        this.canvas.width = width * devicePixelRatio;
        this.canvas.height = height * devicePixelRatio;

        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.scale(devicePixelRatio, devicePixelRatio);
        this.render();
    }

    hitTest(canvasX: number, canvasY: number): IOverlayObject | null {
        const sortedObjects = Array.from(this.objects.values())
            .filter((obj) => obj.visible)
            .sort((a, b) => b.zIndex - a.zIndex);

        for (const obj of sortedObjects) {
            if (obj.hitTest(canvasX, canvasY)) {
                return obj;
            }
        }
        return null;
    }

    getOverlayObject(id: string): IOverlayObject | null {
        return this.objects.get(id) ?? null;
    }

    findOverlayByPrefix(prefix: string): IOverlayObject | null {
        for (const [id, obj] of this.objects) {
            if (id.startsWith(prefix)) return obj;
        }
        return null;
    }

    setObjectVisibility(id: string, visible: boolean): void {
        const obj = this.objects.get(id);
        if (obj && obj.visible !== visible) {
            obj.visible = visible;
            this.render();
        }
    }

    render(): void {
        this.ctx.clearRect(0, 0, this.cssWidth, this.cssHeight);

        const sortedObjects = Array.from(this.objects.values())
            .filter((obj) => obj.visible)
            .sort((a, b) => a.zIndex - b.zIndex);

        for (const obj of sortedObjects) {
            if (!obj.clipByCamera || this.isInCameraClippingRange(obj)) {
                obj.render(this.ctx, this.viewport);
            }
        }
    }

    isInCameraClippingRange(obj: IOverlayObject): boolean {
        const camera = this.viewport.getRenderer(0).getActiveCamera();
        const [near, far] = camera.getClippingRange();

        if (!obj.worldPosition) return true;

        const camPos = camera.getPosition();

        const dx = obj.worldPosition[0] - camPos[0];
        const dy = obj.worldPosition[1] - camPos[1];
        const dz = obj.worldPosition[2] - camPos[2];

        const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

        return distance >= near && distance <= far;
    }

    dispose(): void {
        this.objects.clear();

        if (this.canvas.parentElement) {
            this.canvas.parentElement.removeChild(this.canvas);
        }
    }
}
